import { useMutation, useQueryClient } from '@tanstack/react-query';
import { payrollRollbackService, type RollbackOptions } from '@/services/payroll/payrollRollbackService';
import { payrollKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { accrualHistoryQueryKeys } from '@/hooks/leave/useAccrualHistory';

export function usePayrollRollback() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (options: RollbackOptions) => 
      payrollRollbackService.rollbackPayrollRun(options),
    onSuccess: (result, variables) => {
      if (result.success) {
        // Invalidate all payroll-related queries
        queryClient.invalidateQueries({ queryKey: payrollKeys.runs() });
        queryClient.invalidateQueries({ queryKey: payrollKeys.run(variables.runId) });
        // Invalidate accrual history since rollback reverses accrual entries
        queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
        
        // PHASE 3: Enhanced toast message with accrual reversal count
        const accrualMessage = result.reversedAccruals > 0 
          ? `, ${result.reversedAccruals} leave accrual(s) reversed`
          : '';
        
        toast({
          title: 'Payroll Rolled Back',
          description: `Successfully rolled back payroll run. ${result.deletedPayslips} payslip(s) deleted${accrualMessage}.`,
        });
      } else {
        toast({
          title: 'Rollback Failed',
          description: result.error || 'Failed to rollback payroll run',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Rollback Error',
        description: error.message || 'An unexpected error occurred during rollback',
        variant: 'destructive',
      });
    },
  });
}
